/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * PrioritiesScreen (Upgrade Brief) for Hub
 * Redesigned as a Strategic Intent Panel with card-based selection,
 * drag-and-drop ranking, and impact preview.
 */

import { useState, useMemo } from 'react';
import type { FuturePlan } from '@core/types';
import UpgradeBriefPicker from '@decision-ui/components/shared/UpgradeBriefPicker';
import { PRIORITY_OPTIONS } from '../../../constants/priorityOptions';

const URGENCY_OPTIONS = [
  { value: 'emergency', label: 'Emergency (broken down)' },
  { value: 'soon', label: 'Soon (within weeks)' },
  { value: 'planning', label: 'Planning (within months)' },
  { value: 'future', label: 'Future (exploring options)' },
];

const DISRUPTION_OPTIONS = [
  { value: 'minimal', label: 'Minimal (like-for-like)' },
  { value: 'moderate', label: 'Moderate (some changes)' },
  { value: 'extensive', label: 'Extensive (open to major work)' },
];

export interface PrioritiesResult {
  priorities: Array<{ key: string; rank: number }>;
  urgency: string;
  disruptionTolerance: string;
  futurePlans: FuturePlan[];
}

interface PrioritiesScreenProps {
  initialPriorities?: Array<{ key: string; rank: number }>;
  initialUrgency?: string;
  initialDisruptionTolerance?: string;
  initialFuturePlans?: FuturePlan[];
  onSave: (result: PrioritiesResult) => void;
  onCancel: () => void;
}

// Static impact rules based on priority selections


type FuturePlanChoice = 'yes' | 'no' | 'maybe';

type FuturePlanAnswers = {
  extension: FuturePlanChoice;
  additionalBathroom: FuturePlanChoice;
  electrification: FuturePlanChoice;
};

const FUTURE_PLAN_OPTIONS: Array<{ key: keyof FuturePlanAnswers; label: string; hint: string }> = [
  { key: 'extension', label: 'Planning extension / loft conversion?', hint: 'Affects future heat output headroom.' },
  { key: 'additionalBathroom', label: 'Likely to add another bathroom?', hint: 'Changes DHW capacity needs.' },
  { key: 'electrification', label: 'Considering solar / heat pump within 5 years?', hint: 'Changes electrification fit.' },
];

function deriveFutureAnswers(initialFuturePlans: FuturePlan[]): FuturePlanAnswers {
  const hasPlan = (keys: string[]) => initialFuturePlans.some((p) => keys.includes(p.key));
  return {
    extension: hasPlan(['loft_conversion', 'rear_extension', 'side_extension']) ? 'yes' : 'maybe',
    additionalBathroom: hasPlan(['additional_bathroom']) ? 'yes' : 'maybe',
    electrification: hasPlan(['solar_pv']) ? 'yes' : 'maybe',
  };
}

function buildFuturePlans(answers: FuturePlanAnswers): FuturePlan[] {
  const plans: FuturePlan[] = [];
  if (answers.extension === 'yes') plans.push({ key: 'loft_conversion', horizon: '2_5' });
  if (answers.additionalBathroom === 'yes') plans.push({ key: 'additional_bathroom', horizon: '2_5' });
  if (answers.electrification === 'yes') plans.push({ key: 'solar_pv', horizon: '2_5' });
  return plans;
}

function deriveImpactPreview(
  priorities: Array<{ key: string; rank: number }>,
  disruptionTolerance: string,
): Array<{ text: string; impact: 'positive' | 'negative' | 'neutral' }> {
  const items: Array<{ text: string; impact: 'positive' | 'negative' | 'neutral' }> = [];
  const keys = new Set(priorities.map(p => p.key));

  if (keys.has('low_upfront_cost')) {
    items.push({ text: 'Gas boiler options gain weighting — lower install cost', impact: 'positive' });
    items.push({ text: 'Heat pump loses weighting — higher upfront investment', impact: 'negative' });
  }
  if (keys.has('low_running_cost')) {
    items.push({ text: 'Heat pump gains weighting — lower operating costs if fabric suitable', impact: 'positive' });
  }
  if (keys.has('comfort_even_heat')) {
    items.push({ text: 'System boiler + unvented gains weighting — stored DHW + zoned heat', impact: 'positive' });
    items.push({ text: 'Combi loses weighting if hot water priority also set', impact: 'neutral' });
  }
  if (keys.has('hot_water_performance')) {
    items.push({ text: 'Unvented cylinder options gain weighting — mains-pressure delivery', impact: 'positive' });
    items.push({ text: 'Combi may lose weighting with simultaneous demand', impact: 'negative' });
  }
  if (keys.has('reliability_simplicity')) {
    items.push({ text: 'Proven gas boiler platforms gain weighting', impact: 'positive' });
  }
  if (keys.has('environmental_impact')) {
    items.push({ text: 'Heat pump and hybrid options gain weighting — lower carbon', impact: 'positive' });
  }
  if (keys.has('future_proofing')) {
    items.push({ text: 'Heat pump and hybrid gain weighting — regulation-ready', impact: 'positive' });
  }
  if (keys.has('minimal_disruption') || disruptionTolerance === 'minimal') {
    items.push({ text: 'Like-for-like replacements gain weighting', impact: 'positive' });
    items.push({ text: 'Major upgrades (heat pump) lose weighting', impact: 'negative' });
  }

  return items.slice(0, 5); // Cap at 5 items
}

export default function PrioritiesScreen({
  initialPriorities = [],
  initialUrgency = '',
  initialDisruptionTolerance = '',
  initialFuturePlans = [],
  onSave,
  onCancel,
}: PrioritiesScreenProps) {
  const [priorities, setPriorities] = useState(initialPriorities);
  const [urgency, setUrgency] = useState(initialUrgency);
  const [disruptionTolerance, setDisruptionTolerance] = useState(initialDisruptionTolerance);
  const [futurePlanAnswers, setFuturePlanAnswers] = useState<FuturePlanAnswers>(() => deriveFutureAnswers(initialFuturePlans));
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  const impactPreview = useMemo(
    () => deriveImpactPreview(priorities, disruptionTolerance),
    [priorities, disruptionTolerance],
  );

  const handleChange = (newPriorities: Array<{ key: string; rank: number }>) => {
    setPriorities(newPriorities);
    setHasUnsavedChanges(true);
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowDiscardDialog(true);
    } else {
      onCancel();
    }
  };

  const handleSave = () => {
    setHasUnsavedChanges(false);
    onSave({ priorities, urgency, disruptionTolerance, futurePlans: buildFuturePlans(futurePlanAnswers) });
  };

  return (
    <>
      <div className="hub-screen-overlay">
        <div className="hub-screen" style={{ maxWidth: 720 }} onClick={event => event.stopPropagation()}>
          <div className="hub-screen__header">
            <h2 className="hub-screen__title">Your upgrade brief</h2>
            <button className="hub-screen__close" onClick={handleClose}>&times;</button>
          </div>

          <div className="hub-screen__body">
            <p className="upgrade-brief__intro">
              Every architecture can be right in some homes and wrong in others.
              Capture context first so the recommendation reflects <strong>your home and occupants</strong>, not generic rules.
            </p>

            <div className="hub-note" style={{ marginBottom: '1.25rem' }}>
              <strong>Mission statement:</strong> avoid “spherical chicken in a vacuum” decisions.
              <br />
              <span style={{ opacity: 0.9 }}>
                This tool answers “Is sealed best?”, “Is a combi best?”, “Is hydraulic separation good?” with the same rule:
                <strong> sometimes</strong> — depending on house physics, usage, and constraints.
              </span>
            </div>

            <div className="hub-note" style={{ marginBottom: '1.25rem' }}>
              <strong>Method:</strong>
              <ul style={{ margin: '0.5rem 0 0 1rem', padding: 0 }}>
                <li>Architecture fit first (does it suit this house?).</li>
                <li>Optimisation second (how efficiently does it run?).</li>
                <li>Trade-offs always visible (what improves, what is compromised).</li>
              </ul>
            </div>

            {/* Urgency */}
            <div className="hub-field" style={{ marginBottom: '1rem' }}>
              <label className="hub-field__label">How urgent is the replacement?</label>
              <div className="hub-field__chip-group">
                {URGENCY_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    className={`hub-chip${urgency === opt.value ? ' hub-chip--selected' : ''}`}
                    onClick={() => { setUrgency(opt.value); setHasUnsavedChanges(true); }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Disruption tolerance */}
            <div className="hub-field" style={{ marginBottom: '1.5rem' }}>
              <label className="hub-field__label">How much disruption is acceptable?</label>
              <div className="hub-field__chip-group">
                {DISRUPTION_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    className={`hub-chip${disruptionTolerance === opt.value ? ' hub-chip--selected' : ''}`}
                    onClick={() => { setDisruptionTolerance(opt.value); setHasUnsavedChanges(true); }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Strategic priority picker */}

            <div className="hub-field" style={{ marginBottom: '1.5rem' }}>
              <label className="hub-field__label">Future plans (optional but improves future fit)</label>
              <div style={{ display: 'grid', gap: '0.85rem' }}>
                {FUTURE_PLAN_OPTIONS.map((question) => (
                  <div key={question.key} className="hub-note" style={{ padding: '0.75rem' }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>{question.label}</div>
                    <div className="hub-field__chip-group" style={{ marginBottom: 4 }}>
                      {(['yes', 'no', 'maybe'] as const).map((choice) => (
                        <button
                          key={choice}
                          className={`hub-chip${futurePlanAnswers[question.key] === choice ? ' hub-chip--selected' : ''}`}
                          onClick={() => {
                            setFuturePlanAnswers((prev) => ({ ...prev, [question.key]: choice }));
                            setHasUnsavedChanges(true);
                          }}
                        >
                          {choice[0].toUpperCase() + choice.slice(1)}
                        </button>
                      ))}
                    </div>
                    <div style={{ fontSize: '0.82rem', opacity: 0.8 }}>{question.hint}</div>
                  </div>
                ))}
              </div>
            </div>

            <UpgradeBriefPicker
              options={PRIORITY_OPTIONS}
              value={priorities}
              onChange={handleChange}
              maxSelections={3}
              impactPreview={impactPreview}
            />
          </div>

          <div className="hub-screen__footer">
            <button
              type="button"
              className="hub-screen__button hub-screen__button--secondary"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="hub-screen__button hub-screen__button--primary"
              onClick={handleSave}
            >
              Save upgrade brief
            </button>
          </div>
        </div>
      </div>

      {/* Discard changes dialog */}
      {showDiscardDialog && (
        <div className="hub-screen-overlay" style={{ zIndex: 1001 }}>
          <div className="hub-screen hub-screen--dialog" onClick={e => e.stopPropagation()}>
            <div className="hub-screen__header">
              <h2 className="hub-screen__title">Discard changes?</h2>
            </div>
            <div className="hub-screen__body">
              <p>You have unsaved changes. Are you sure you want to discard them?</p>
            </div>
            <div className="hub-screen__footer">
              <button
                className="hub-screen__button hub-screen__button--secondary"
                onClick={() => setShowDiscardDialog(false)}
              >
                Keep editing
              </button>
              <button
                className="hub-screen__button hub-screen__button--danger"
                onClick={() => {
                  setShowDiscardDialog(false);
                  setHasUnsavedChanges(false);
                  onCancel();
                }}
              >
                Discard changes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
